import { useEffect, useRef } from "react";
import { SetterOrUpdater } from "recoil";
import { NewRequisitionType } from "../NewRequisition/types";
import { DismissRedrawAlert } from "./Services/RequisitionAlertService";
import { RefreshNewRequisitionOrders } from "./Services/GridService";
import { Toast } from "./useToast";

const REDRAW_ALERT_UNDO_DURATION_MS = 2000;

interface UseRedrawAlertDismissalParams {
  token?: string;
  setLoggedOut: (inactivity: boolean) => void;
  setAllOrderedRequisitions: SetterOrUpdater<NewRequisitionType[]>;
  setOrderedRequestComplete: SetterOrUpdater<boolean>;
  toast: Toast;
}

interface RedrawAlertDismissal {
  handleRedrawDismissFromAlert: (order: NewRequisitionType) => void;
}

interface PendingRedrawDismissal {
  persist: () => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

const setRedrawAlertDismissed = (
  setAllOrderedRequisitions: SetterOrUpdater<NewRequisitionType[]>,
  orderId: string,
  dismissed: boolean,
) => {
  setAllOrderedRequisitions((prev) =>
    prev.map((requisition) =>
      requisition.unity_complete_id === orderId
        ? { ...requisition, is_redraw_alert_dismissed: dismissed }
        : requisition,
    ),
  );
};

export const useRedrawAlertDismissal = ({
  token,
  setLoggedOut,
  setAllOrderedRequisitions,
  setOrderedRequestComplete,
  toast,
}: UseRedrawAlertDismissalParams): RedrawAlertDismissal => {
  const pendingTimeoutsRef = useRef(new Map<string, PendingRedrawDismissal>());

  useEffect(
    () => () => {
      pendingTimeoutsRef.current.forEach(({ timeoutId }) => {
        clearTimeout(timeoutId);
      });
      pendingTimeoutsRef.current.clear();
    },
    [],
  );

  const handleRedrawDismissFromAlert = (order: NewRequisitionType) => {
    if (!token || !order.source_req_identifier) return;
    const sourceReqIdentifier = order.source_req_identifier;

    Array.from(pendingTimeoutsRef.current.entries()).forEach(
      ([orderId, pendingDismissal]) => {
        if (orderId === order.unity_complete_id) {
          return;
        }

        clearTimeout(pendingDismissal.timeoutId);
        pendingDismissal.persist();
      },
    );

    const existingPendingDismissal = pendingTimeoutsRef.current.get(
      order.unity_complete_id,
    );
    if (existingPendingDismissal) {
      clearTimeout(existingPendingDismissal.timeoutId);
      pendingTimeoutsRef.current.delete(order.unity_complete_id);
    }

    setRedrawAlertDismissed(
      setAllOrderedRequisitions,
      order.unity_complete_id,
      true,
    );

    const undoRedrawDismissal = () => {
      const pendingDismissal = pendingTimeoutsRef.current.get(
        order.unity_complete_id,
      );
      if (pendingDismissal) {
        clearTimeout(pendingDismissal.timeoutId);
        pendingTimeoutsRef.current.delete(order.unity_complete_id);
      }

      setRedrawAlertDismissed(
        setAllOrderedRequisitions,
        order.unity_complete_id,
        false,
      );
    };

    const persistRedrawDismissal = () => {
      pendingTimeoutsRef.current.delete(order.unity_complete_id);
      (async () => {
        try {
          const response = await DismissRedrawAlert(
            token,
            sourceReqIdentifier,
            setLoggedOut,
          );
          if (!response.ok) throw new Error("Failed to dismiss redraw alert");
        } catch {
          await RefreshNewRequisitionOrders(
            token,
            setAllOrderedRequisitions,
            setLoggedOut,
            setOrderedRequestComplete,
          );
          toast.showError("Could not remove redraw alert. Please try again.");
        }
      })();
    };

    const timeoutId = setTimeout(
      persistRedrawDismissal,
      REDRAW_ALERT_UNDO_DURATION_MS,
    );
    pendingTimeoutsRef.current.set(order.unity_complete_id, {
      persist: persistRedrawDismissal,
      timeoutId,
    });

    toast.showToast("Redraw alert removed.", {
      undoAction: undoRedrawDismissal,
      autoHideDuration: REDRAW_ALERT_UNDO_DURATION_MS,
    });
  };

  return { handleRedrawDismissFromAlert };
};
